import React from 'react';
import { fmtBRL, fmtMonthShort } from '../utils/adjustments';
import { fmtDate } from '../utils/formatters';

/**
 * A PRÉVIA DE UM REAJUSTE QUE AINDA NÃO FOI GRAVADO.
 *
 * Serve aos dois caminhos que aplicam valor, porque eles devolvem o MESMO
 * objeto: o apostilamento de órgão público (`:id/apostila`) e a aplicação de
 * termo aditivo (`aditivos/:id/aplicar`, backend #238). Não é coincidência — o
 * backend passa os dois pelo mesmo `registerApostila` de propósito, para não
 * haver um segundo caminho de calcular valor que divergisse no primeiro
 * arredondamento.
 *
 * Duas telas desenhando o mesmo objeto acabariam mostrando coisas diferentes
 * sobre a mesma conta, e a prévia existe justamente para ser conferida antes de
 * mexer em MRR, item, tarifa de consumo e fatura complementar.
 */

interface Props {
  preview: any;
}

export default function PreviaDoReajuste({ preview: p }: Props) {
  if (!p) return null;
  const recorrentes = (p.contract?.items || []).filter((i: any) => i.type !== 'PS').length;
  const meses: string[] = p.retro?.months || [];
  return (
    <div className='bg-slate-800/60 border border-slate-700 rounded-lg p-4 text-sm space-y-2'>
      <p className='text-white font-medium'>Prévia</p>
      <p className='text-slate-300'>
        Mensal da assinatura: {fmtBRL(p.contract?.mrrBefore)} →{' '}
        <span className='text-white font-medium'>{fmtBRL(p.contract?.mrrAfter)}</span> · {recorrentes} item(ns)
        recorrente(s) reajustado(s) item a item.
      </p>
      {meses.length ? (
        <p className='text-slate-300'>
          Retroativo: {meses.length} competência(s) × {fmtBRL(p.retro.monthlyDiff)} ={' '}
          <span className='text-white font-medium'>{fmtBRL(p.retro.total)}</span> (
          {fmtMonthShort(meses[0] + '-01')} a {fmtMonthShort(meses[meses.length - 1] + '-01')}; última faturada:{' '}
          {p.lastBilledMonth ? fmtMonthShort(p.lastBilledMonth + '-01') : '—'})
        </p>
      ) : (
        <p className='text-slate-300'>Sem competências faturadas a menor: nenhuma complementar.</p>
      )}
      {p.invoice && (
        <p className='text-slate-400 text-xs'>
          Complementar: competência {p.invoice.referenceMonth}, período {p.invoice.periodStart} a{' '}
          {p.invoice.periodEnd}, vencimento {p.invoice.dueDate},{' '}
          {p.invoice.paymentMethod || 'sem forma de pagamento no cadastro'}.
        </p>
      )}
      <p className='text-slate-400 text-xs'>
        Estado final da anualidade: <span className='text-white'>{p.statusAfter}</span>
      </p>
      {p.scheduled && (
        <p className='text-blue-300 text-xs'>
          Agendada: nada muda no contrato hoje. Em {fmtDate(p.applyAt)} o cron aplica o valor, recalcula o
          retroativo com a última competência faturada daquele dia e gera a complementar, se houver.
        </p>
      )}
      {(p.warnings || []).map((w: string, i: number) => (
        <p key={i} className='text-amber-400 text-xs'>⚠ {w}</p>
      ))}
      {p.retro?.lines?.length > 0 && (
        <pre className='text-[11px] text-slate-500 whitespace-pre-wrap mt-2'>{p.retro.lines.join('\n')}</pre>
      )}
    </div>
  );
}
